import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import produce from 'immer';
import axios from 'axios';
import logger from '../common/logger';
import { LEARN_LIVE_SERVICES_HOST } from '../common/constants';
import { getEnvironmentConstant } from '../common/utilities';

export const coachingCatalogDetailSliceInitialState = {
  result: {},
  isLoaderVisible: null,
  hasError: false,
  errorMessage: null,
};

export const fetchCoachingCatalogDetailData = createAsyncThunk(
  'coachingCatalogDetailData',
  async ({ courseId, env, isPreview = false },
    { rejectWithValue }) => {
    try {
      // Add preview query parameter if in preview mode
      const previewParam = isPreview ? '?preview=true' : '';
      const endpoint = `${getEnvironmentConstant({ env, key: LEARN_LIVE_SERVICES_HOST })}/cso/digitalcatalog/v1/${courseId}${previewParam}`;
      const coachingCatalogDetailResponse = await axios.get(endpoint);
      if (coachingCatalogDetailResponse?.status !== 200) {
        throw new Error('Invalid response', coachingCatalogDetailResponse);
      }
      return coachingCatalogDetailResponse?.data;
    } catch (error) {
      logger.error('Error in fetchCoachingCatalogData', {
        error: error.message,
        stack: error.stack,
        env,
        courseId,
      });

      return rejectWithValue({
        error: error.message,
        status: 'Error',
      });
    }
  },
);

export const coachingCatalogDetailSlice = createSlice({
  name: 'coachingCatalogDetailData',
  initialState: coachingCatalogDetailSliceInitialState,
  reducers: {
    clearResults: (state) => {
      const newState = produce(state, (draftState) => {
        draftState.result = {};
        draftState.result.status = 'cleared';
      });
      return newState;
    },
    clearError: (state) => {
      const newState = produce(state, (draftState) => {
        draftState.hasError = false;
        draftState.errorMessage = null;
      });
      return newState;
    },
  },
  extraReducers: {
    [fetchCoachingCatalogDetailData.fulfilled]: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.isLoaderVisible = false;
        draftState.result = action?.payload ?? {};
        draftState.hasError = false;
        draftState.errorMessage = null;
        // Update filters based on payload
      });
      logger.debug(newState);
      return newState;
    },
    [fetchCoachingCatalogDetailData.rejected]: (state, action) => {
      logger.error('Error in fetchCoachingCatalogData: ', action.error);
      return {
        ...state,
        isLoaderVisible: false,
        hasError: true,
        errorMessage: action.payload?.error || action.error?.message || 'An error occurred while fetching coaching catalog data',
      };
    },
    [fetchCoachingCatalogDetailData.pending]: (state) => produce(state, (draftState) => {
      draftState.isLoaderVisible = true;
    }),
  },
});

export const getCoachingCatalogDetailResult = (state) => state.coachingCatalogDetailData.result;

export const isCoachingLoaderVisible = (state) => state.coachingCatalogDetailData.isLoaderVisible;

export const getCoachingCatalogDetailError = (state) => ({
  hasError: state.coachingCatalogDetailData()?.hasError || false,
  errorMessage: state.coachingCatalogDetailData?.errorMessage || null,
});

export default coachingCatalogDetailSlice.reducer;
